<template>
  <div>
    <b-row class="pl-2 pr-2">
      <b-col class="b-avatar-img mb-3 col-md-12 col-12 text-left">
        <div class="tt-alert-block">
          <transition
            name="alert-slide-fade"
            leave-active-class="animate__animated animate__fadeOutUp"
          >
            <b-alert
              variant="danger"
              show=""
              v-if="savingStatus === 'failedSave'"
              >An error occurred, please try again</b-alert
            >
          </transition>
        </div>
        <h3 class="text-center font-weight-bold mb-0">Log New Trip</h3>
        <div class="col-12">
          <b-col
            class="px-0 justify-content-center text-center d-flex"
            v-if="allPlaces.length == 0"
          >
            <div class="mt-3">
              <h1>Hello :)</h1>
              <p>Get started by setting up your traveling locations</p>
              <b-button
                :to="'/add-place'"
                variant="warning"
                class="w-100 py-2 btn-new text-white mb-3 tt-border-radius"
                >Setup locations</b-button
              >
            </div>
          </b-col>
        </div>
        <validation-observer v-slot="{ invalid }" ref="observer">
          <b-form @submit.prevent="saveTrip" class="tt-mt">
            <div class="row">
              <b-form-group
                label="Pick your car"
                label-class="pb-0"
                id="input-9"
                class="col-md-12 mb-2"
                v-if="carList.length > 1"
              >
                <validation-provider :rules="'required'" v-slot="{ errors }">
                  <select
                    :class="{ 'is-invalid': errors[0] }"
                    class="custom-select"
                    name=""
                    v-model="trip.vehicle_pouch_id"
                  >
                    <option
                      v-for="car in carList"
                      :value="car._id"
                      :key="car._id"
                      :selected="car._id == trip.vehicle_pouch_id"
                      >{{ car.make }}</option
                    >
                  </select>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label-class="pb-0"
                label="Leaving from"
                id="input-group-2"
                class="col-md-12 mb-2"
              >
                <validation-provider rules="required" v-slot="{ errors }">
                  <select
                    :class="{ 'is-invalid': errors[0] }"
                    class="custom-select"
                    name=""
                    id="input-1"
                    :disabled="allPlaces.length === 0"
                    v-model.lazy="trip.departure_location_id"
                  >
                    <option value="">Select from saved places</option>
                    <option
                      v-for="place of placesShown"
                      :value="place._id"
                      :key="place._id"
                      >{{ place.name }}</option
                    >
                  </select>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
              </b-form-group>
              <b-form-group
                label="Going to"
                label-class="pb-0"
                id="input-group-3"
                class="col-md-12 mb-2"
              >
                <validation-provider
                  :rules="'required|is:' + trip.departure_location_id"
                  v-slot="{ errors }"
                >
                  <select
                    :class="{ 'is-invalid': errors[0] }"
                    class="custom-select"
                    name=""
                    id="input-2"
                    :disabled="allPlaces.length === 0"
                    v-model="trip.arrival_location_id"
                  >
                    <option value="">Select from saved places</option>
                    <option
                      v-for="place of placesShown"
                      :value="place._id"
                      :key="place._id"
                      >{{ place.name }}</option
                    >
                  </select>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
              </b-form-group>
            </div>
            <div class="row">
              <b-form-group
                label="Odometer on departure"
                class="col-md-12 mb-2"
                :label-class="{ 'mobile-label': isMobile }"
              >
                <validation-provider
                  :rules="
                    'required|integer|min:' +
                      (!isAnEdit && lastRecord ? lastRecord.final_odometer : 0)
                  "
                  v-slot="{ errors }"
                >
                  <b-form-input
                    placeholder="Km."
                    id="input-3"
                    name="odoR"
                    type="number"
                    :disabled="allPlaces.length === 0"
                    v-model="trip.initial_odometer"
                    :class="{ 'is-invalid': errors[0] }"
                  ></b-form-input>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
                <small v-if="lastRecord && isAnEdit === false">
                  {{
                    "Last Odometer " + lastRecord.final_odometer + " Km"
                  }}</small
                >
              </b-form-group>
            </div>
            <div class="row">
              <b-form-group
                label="Odometer on arrival"
                class="col-md-12 mb-2"
                label-class="pb-0"
              >
                <validation-provider
                  :rules="
                    'required|min:' + (parseInt(trip.initial_odometer) + 1)
                  "
                  v-slot="{ errors }"
                  mode="lazy"
                >
                  <b-form-input
                    placeholder="Km."
                    id="input-4"
                    type="number"
                    :disabled="allPlaces.length === 0"
                    v-model="trip.final_odometer"
                    :class="{ 'is-invalid': errors[0] }"
                  ></b-form-input>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
              </b-form-group>
            </div>
            <div class="row">
              <b-form-group class="col-md-12 mb-2">
                <validation-provider v-slot="{ errors }" rules="required">
                  <b-form-radio
                    name="is_business"
                    v-model="trip.is_business"
                    :value="true"
                    class="pr-3"
                    inline
                    >Business</b-form-radio
                  >
                  <b-form-radio
                    name="is_business"
                    v-model="trip.is_business"
                    :value="false"
                    class="pr-3"
                    inline
                    >Private</b-form-radio
                  >
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
              </b-form-group>
            </div>
            <div class="row">
              <b-form-group
                label="Reason for trip"
                class="col-md-12 mb-2"
                label-class="pb-0"
                v-if="trip.is_business"
              >
                <validation-provider v-slot="{ errors }">
                  <select
                    :class="{ 'is-invalid': errors[0] }"
                    class="custom-select"
                    name=""
                    id="input-7"
                    :disabled="allPlaces.length === 0"
                    v-model="trip.description"
                  >
                    <option
                      v-for="reason in reasons"
                      :value="reason"
                      :selected="trip.description === reason"
                      :key="reason"
                    >
                      {{ reason }}</option
                    >
                    <option
                      value="other"
                      :selected="
                        trip.description &&
                          reasons.indexOf(trip.description) == -1
                      "
                    >
                      Other</option
                    >
                  </select>
                </validation-provider>
              </b-form-group>
              <b-form-group class="col-md-12 mb-2">
                <validation-provider
                  v-slot="{ errors }"
                  :rules="'is_not:Please specify'"
                >
                  <b-form-input
                    v-if="trip.description === 'other'"
                    id="input-7"
                    type="text"
                    :disabled="allPlaces.length === 0"
                    placeholder="Please specify"
                    v-model.lazy="trip.other_description"
                    :class="{ 'is-invalid': errors[0] }"
                  ></b-form-input>
                </validation-provider>
              </b-form-group>
            </div>
            <div class="row">
              <b-form-group
                label="Time"
                class="col-md-12 mb-2"
                label-class="pb-0"
              >
                <validation-provider rules="required" v-slot="{ errors }">
                  <datetime
                    type="datetime"
                    :format="'ff'"
                    v-model="trip_time"
                    :disabled="allPlaces.length === 0"
                    :input-class="{
                      'form-control': true,
                      'is-invalid': errors[0]
                    }"
                  >
                  </datetime>
                  <span class="text-danger">{{ errors[0] }}</span>
                </validation-provider>
                <div>
                  <button
                    type="submit"
                    class="align-content-start mt-5 btn text-white btn-success pl-5 pr-5 tt-border-radius w-100"
                    :disabled="invalid"
                  >
                    Save Trip
                  </button>
                </div>
              </b-form-group>
            </div>
          </b-form>
        </validation-observer>
        <div class="mt-3">
          <hr />
          <trip :limit="true" @changeEditedTrip="updateEditedTrip"></trip>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { Datetime } from "vue-datetime";
import { DateTime as TimeFormatter } from "luxon";
import { ValidationProvider, extend, ValidationObserver } from "vee-validate";
import { required, min, integer, is } from "vee-validate/dist/rules";
import "vue-datetime/dist/vue-datetime.css";
import Trip from "@/views/Trip";
import { mapGetters, mapState } from "vuex";
import moment from "moment";
import sha1 from "js-sha1";

extend("required", {
  ...required,
  message: "This field is required"
});
extend("integer", {
  ...integer,
  message: "Please input a number"
});

extend("is", {
  ...is,
  validate(value, args) {
    // console.log(args, value)
    return value !== args.other;
  },
  message:
    "Your destination point is the same as your starting point. Please select another name or add another location to your places"
});

extend("min", {
  ...min,
  validate(value, args) {
    //disable this validation when editing an existing trip
    return value >= args.length;
  },
  message: "Your odometer is lesser than what you recorded previously"
});

export default {
  name: "NewTrip",
  components: { Datetime, ValidationProvider, ValidationObserver, Trip },
  data() {
    return {
      places: "",
      newTrip: {
        _id: new Date().toISOString(),
        type: "trip",
        is_synced: false,
        is_default: false,
        status: 0,
        hash: "",
        vehicle_pouch_id: "",
        departure_location_id: "",
        arrival_location_id: "",
        initial_odometer: "",
        final_odometer: "",
        is_business: true,
        belongs_to_default_car: true,
        description: "Site visit",
        other_description: "",
        trip_time: new Date().toISOString(),
        saved_at: new Date().toISOString()
      },
      newEditedTrip: "",
      reasons: [
        "Site visit",
        "Warehouse visit",
        "Client meeting",
        "Team meeting",
        "Training course",
        "Strategy session"
      ]
    };
  },
  props: {
    editableTrip: {
      type: Object
    }
  },

  created() {
    this.$store.dispatch("place/getPlacesAction");
    this.$store.dispatch("trip/getTripsByCarAction");
    this.newTrip.hash = sha1(new Date().toISOString() + Math.random());
  },
  beforeRouteLeave(to, from, next) {
    delete this.$route.params.editableTrip; // delete the prop value passed when editing
    //
    if (to.name !== "edit-trip") {
      this.$store.commit("trip/resetValue");
    }
    next();
  },

  methods: {
    saveTrip: function() {
      this.beforeSaving();
      this.$store.commit("trip/save", this.trip);
      this.$refs["observer"].reset();
      this.resetForm();
      this.$root.$emit("newRecordSaved");
      this.$router.push({ name: "trips" });
    },
    resetForm: function() {
      this.trip._id = new Date().toISOString();
      this.trip.departure_location_id = "";
      this.trip.arrival_location_id = "";
      this.trip.initial_odometer = "";
      this.trip.final_odometer = "";
      this.trip.description = "";
      this.trip.is_business = true;
      this.trip.belongs_to_default_car = true;
      this.trip.vehicle_pouch_id = "default";
      this.trip.trip_time = new Date().toISOString();
      this.trip.saved_at = new Date().toISOString();
      this.newTrip.hash = sha1(new Date().toISOString() + Math.random());
    },

    setDefaultVehicleId: function() {
      setTimeout(() => {
        var lastVehicle = this.defaultVehicle;
        this.newTrip.vehicle_pouch_id = lastVehicle._id;
      }, 1000);
    },
    //ensure edited trip is set on offline mode in case it was synced before
    setIsSyncedFalse: function() {
      this.$route.params.editableTrip.is_synced = false;
    },
    updateEditedTrip: function(val) {
      this.newEditedTrip = val;
    },
    beforeSaving: function() {
      //changing date format before saving
      this.trip.trip_time = TimeFormatter.fromISO(this.trip.trip_time).toFormat(
        "y-MM-dd H:m:s"
      ); //Todo : could be done in a better way;
      this.trip.saved_at = TimeFormatter.fromISO(this.trip.saved_at).toFormat(
        "y-MM-dd H:m:s"
      ); //Todo : could be done in a better way;
      this.trip.description =
        this.trip.description !== "other"
          ? this.trip.description
          : this.trip.other_description; //if user specified reasons other than the ones on the dropdown
    },
    momentJs() {
      return moment;
    }
  },

  computed: {
    savedRecords() {
      try {
        return this.$store.state.trip.all;
      } catch (e) {
        return {};
      }
    },
    isAnEdit: function() {
      return !!this.editableTrip;
    },
    ...mapGetters({
      frequentDeparture: "trip/getCommonDeparture",
      frequentArrival: "trip/getFrequentArrival",
      carsArr: "car/getCarsArray",
      lastRecord: "trip/getLastestTrip",
      defaultVehicle: "car/getDefaultCar",
      defaultPlace: "place/getDefaultPlace",
      getDefaultCarsList: "car/getDefaultCarsList",
      getUsersSavedCarsList: "car/getUsersSavedCarsList"
    }),
    ...mapState({
      allPlaces: state => state.place.all,
      predictedMileage: state => state.trip.distance,
      error: state => state.trip.actionErrorMsg,
      success: state => state.trip.actionSuccessMsg
    }),
    placesShown: function() {
      try {
        let userSavedPlace = this.allPlaces.filter(place => {
          return place.is_default === false;
        });
        return [...userSavedPlace];
      } catch {
        return [];
      }
    },
    trip: function() {
      if (this.newEditedTrip) {
        window.scrollTo(0, 0);
        return this.newEditedTrip;
      } else if ("editableTrip" in this.$route.params) {
        this.setIsSyncedFalse();
        window.scrollTo(0, 0);
        return this.$route.params.editableTrip;
      } else {
        //make sure you set default vehicle id on the new records...
        //The user can still change it on the field if they have multiple vehicles
        this.setDefaultVehicleId();
        return this.newTrip;
      }
    },
    isMobile: function() {
      return window.innerWidth <= 768;
    },
    carList: function() {
      return [this.defaultVehicle, ...this.getUsersSavedCarsList];
    },
    savingStatus() {
      return this.error || this.success;
    },
    trip_time: {
      get: function() {
        return moment(this.trip.trip_time).toISOString();
      },
      set: function(time) {
        this.trip.trip_time = time;
      }
    }
  },
  watch: {
    frequentArrival: function(value) {
      this.trip.arrival_location_id = value;
    },

    frequentDeparture: function(departure) {
      this.trip.departure_location_id = departure;
      //get frequent arrival
      this.$store.dispatch(
        "trip/getFrequentArrivalAction",
        this.trip.departure_location_id
      );
    },

    //predict odo's
    lastRecord: function(value) {
      if (!this.isAnEdit) {
        this.trip.initial_odometer = value.final_odometer;
        this.trip.final_odometer =
          parseInt(value.final_odometer) + parseInt(this.predictedMileage);
      }
    },
    //fire the get arrival action to predict arrival when departure was changed manaually
    "trip.departure_location_id": function(value) {
      this.$store.dispatch("trip/getFrequentArrivalAction", value);

      //update odometer values if starting point changed and ending point wont
      if (!this.trip.arrival_location_id === "") {
        this.$store.dispatch("trip/getDistanceAction", [
          this.trip.departure_location_id,
          value
        ]);
      }
    },

    //fire the get distance action to predict the mileage when after arrival was changed manually
    "trip.arrival_location_id": function(value) {
      this.$store.dispatch("trip/getDistanceAction", [
        this.trip.departure_location_id,
        value
      ]);
    },

    "trip.is_business": function(value) {
      if (value === false) {
        this.trip.description = "Private";
      } else {
        this.trip.description = "";
      }
    },

    predictedMileage: function(value) {
      this.trip.final_odometer =
        parseInt(this.trip.initial_odometer) + parseInt(value);
    },

    "trip.trip_time": function(value) {
      "editableTrip" in this.$route.params
        ? new Date(value).toISOString()
        : value;
    },

    "trip.saved_at": function() {
      this.trip.saved_at = new Date().toISOString();
    },
    "trip.vehicle_pouch_id": function(pouch_car_id) {
      this.$store.dispatch("trip/getTripsByCarAction", pouch_car_id);
      this.trip.belongs_to_default_car =
        this.carsArr[pouch_car_id].is_default_car || false;
      // console.log(pouch_car);
    },
    defaultVehicle: function(value) {
      this.trip.vehicle_pouch_id = value._id;
      // this.$store.dispatch("expense/getLastTripAction", value._id);
    },

    savingStatus: function() {
      this.$store.commit("trip/clearMes");
    }
  },
  beforeDestroy() {
    this.$store.commit("trip/resetValue");
    this.newEditedTrip = "";
  }
};
</script>

<style scoped>
::placeholder {
  color: #0b2e13;
}
.mobile-label {
  font-size: 3em !important;
}
</style>
